/**
 * Substitute named `{{token}}` placeholders in resolved theory copy.
 *
 * The generalisation of {@link ./interpolate-target.ts interpolateTarget}, which stays exactly as
 * it is because `{{target}}` is its own contract with its own consumers. The Pitch Trainer's §12
 * entries are parameterised on `{{cents}}`, `{{low}}` and `{{high}}`, and they carry the same two
 * honesty guards:
 *
 *   - A token with no value is STRIPPED, not left raw. A singer must never read `{{cents}}`; the
 *     copy degrades to a slightly more generic sentence that is still true.
 *   - Any placeholder the caller did not name is stripped too. The guard is on the rendered
 *     string, not on the caller remembering every token an entry happens to carry — the entry is
 *     locked copy that can gain a placeholder without this call site changing.
 *
 * `0` is a value; `undefined` and `''` are absences.
 *
 * @example
 *   interpolateTokens('drifted {{cents}} cents flat', { cents: 30 })  // 'drifted 30 cents flat'
 *   interpolateTokens('drifted {{cents}} cents flat', {})             // 'drifted cents flat'
 */
export const interpolateTokens = (copy: string, tokens: Record<string, string | number | undefined>): string => {
  const filled = Object.entries(tokens).reduce(
    (text, [name, value]) =>
      text.split(`{{${name}}}`).join(value === undefined || value === '' ? '' : String(value)),
    copy
  )
  return (
    filled
      .replace(/\{\{[a-zA-Z]+\}\}/g, '')
      /** Horizontal whitespace only — a stripped token must not join two lines into one. */
      .replace(/[^\S\n]+/g, ' ')
      .trim()
  )
}
